/**
 * SVD-native color grouping — peer of `svd-cascade-helper`, for the SVD buy-box flow only.
 *
 * Parity with the legacy color-grouping split (`getUniqueColorGroupings` + `updateProductOptionsWithNewColorOptions`,
 * which break the single Color option into a "Core Color" row + one row per group): here the split is driven by the
 * V2 per-value `group` field (`SvdVdaValue.group`, proto `VdaValue.group`) on the PRIMARY-SWATCH VDA only. It is a
 * pure DISPLAY split — value identity / cascade / selection all stay keyed on the value `key`; grouping only decides
 * which selector row a value renders in.
 *
 * Reads only SVD shapes (`SvdVda` + `SvdVdaValue.group`); never a legacy `ProductOption` / `colorGrouping` variant field.
 */

import type { SvdVda } from '../domains/svd/svd-product.types'

export interface SvdColorGroup {
  /** Group identity — the proto `group.value`; empty string for the ungrouped "core" bucket. */
  key: string
  /** Legend label for the row — the group's `localisedValue` (or the caller's core label for the core bucket). */
  displayName: string
  /** Value keys in this group, in the VDA's display order (`.order`). */
  valueKeys: string[]
}

/**
 * Split a VDA's values into ordered color-group rows — the **core** (ungrouped) bucket first, then each named group
 * in first-encounter order (mirroring the legacy core-then-groups layout). Returns `undefined` when NO value carries
 * a `group`, so the caller renders the VDA as a single row (the common case).
 *
 * `coreLabel` is the caller's resolved "Core Color" copy (kept out of this pure helper for i18n).
 */
export function getSvdColorGroups(vda: SvdVda, coreLabel: string): SvdColorGroup[] | undefined {
  const orderedValues = [...vda.values].sort((a, b) => a.order - b.order)
  if (!orderedValues.some(value => value.group?.value)) return undefined

  const coreKeys: string[] = []
  const namedGroups = new Map<string, SvdColorGroup>()

  orderedValues.forEach(value => {
    const group = value.group
    if (!group?.value) {
      coreKeys.push(value.key)
      return
    }
    const existing = namedGroups.get(group.value)
    if (existing) {
      existing.valueKeys.push(value.key)
    } else {
      namedGroups.set(group.value, {
        key: group.value,
        displayName: group.localisedValue || group.value,
        valueKeys: [value.key],
      })
    }
  })

  const groups: SvdColorGroup[] = []
  if (coreKeys.length > 0) groups.push({ key: '', displayName: coreLabel, valueKeys: coreKeys })
  groups.push(...namedGroups.values())
  return groups
}
